import axios from 'axios';
import { MessagePlugin } from 'tdesign-react';
import { clearPortalAuth, getPortalToken, isPortalPage } from './auth';

// 后端统一响应结构（与 @tencent/ssd-nest-app 默认契约一致）
interface ApiResponse<T = unknown> {
  Response: {
    Data: T;
    Error?: {
      Code: string;
      Message: string;
    };
  };
  RequestID: string;
}

/**
 * 扩展 axios 配置：silent=true 时，拦截器不弹 toast、不做 401/403 跳转。
 * 用于"探测型"请求（如登录页冷启动判断身份），失败由调用方静默处理。
 */
declare module 'axios' {
  // eslint-disable-next-line @typescript-eslint/no-empty-object-type
  export interface AxiosRequestConfig {
    silent?: boolean;
  }
}

const http = axios.create({
  baseURL: '/api',
  timeout: 30000,
  headers: {
    'Content-Type': 'application/json',
  },
});

/**
 * 请求拦截器：仅当处于门户页 (/portal/*) 时注入 Authorization。
 * 后台管理页走太湖票据（由前端 nginx / 部署侧自动注入 x-tai-identity），无需在前端加 header。
 */
http.interceptors.request.use(
  (config) => {
    if (isPortalPage()) {
      const token = getPortalToken();
      if (token) {
        config.headers = config.headers || {};
        config.headers.Authorization = `Bearer ${token}`;
      }
    }
    return config;
  },
  (error) => Promise.reject(error),
);

/**
 * 响应拦截器
 * - 业务侧：解构 Response.Data；命中 Response.Error 即 toast + reject
 * - 鉴权侧：
 *    401（未登录/过期）→ 清门户态，门户页跳 /portal/login
 *    403（无权限）     → 门户页跳 /portal/no-permission；后台跳 /403
 *                       （后台 403 含义：当前太湖用户不在 ADMIN_USERS 白名单）
 */
http.interceptors.response.use(
  ((response: any) => {
    const res = response.data as ApiResponse;
    const silent = response.config?.silent === true;
    if (res.Response?.Error) {
      const errorMsg = res.Response.Error.Message || '请求失败';
      if (!silent) MessagePlugin.error(errorMsg);
      return Promise.reject(new Error(errorMsg));
    }
    return res.Response?.Data;
  }) as any,
  (error) => {
    const status = error.response?.status;
    const errorData = error.response?.data?.Response?.Error;
    const message = errorData?.Message || error.message || '网络请求失败';
    const silent = error.config?.silent === true;

    // silent 请求：完全不打扰用户，由调用方处理
    if (silent) {
      if (status === 401) clearPortalAuth();
      return Promise.reject(error);
    }

    // 401：未登录 / 票据过期 —— 统一清门户态并跳登录页（无论门户还是后台）
    if (status === 401) {
      clearPortalAuth();
      // 避免登录页自身触发 401 时无限刷新
      if (!window.location.pathname.startsWith('/portal/login')) {
        window.location.replace('/portal/login');
      }
      return Promise.reject(error);
    }

    if (status === 403) {
      if (isPortalPage()) {
        // 门户接口的 403 一律视为账号未授权，跳门户无权限页
        if (!window.location.pathname.startsWith('/portal/no-permission')) {
          window.location.replace('/portal/no-permission');
        }
      } else {
        // 后台接口的 403 = 不在 ADMIN_USERS 白名单，统一跳后台无权限页
        if (window.location.pathname !== '/403') {
          window.location.replace('/403');
        }
      }
      return Promise.reject(error);
    }

    MessagePlugin.error(message);
    return Promise.reject(error);
  },
);

export default http;
